import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Star, MessageSquare, AlertTriangle, CalendarClock, FileText, Send,
  QrCode, Link2, Download, Sparkles, RefreshCw, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Button } from "@/components/ui/button";
import api, { arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatCard from "@/components/common/StatCard";
import ChartCard, { chartTooltipStyle } from "@/components/common/ChartCard";
import { CardSkeletonGrid } from "@/components/common/Loading";
import { useAuth } from "@/context/AuthContext";

const SENTIMENT_COLORS = ["#22c55e", "#94a3b8", "#ef4444"];

export default function ClientDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {}); }, []);

  if (!data) return <div className="space-y-6"><PageHeader title="Dashboard" /><CardSkeletonGrid /></div>;
  const { cards = {}, charts = {}, health = {} } = data;
  charts.reviewGrowth = arr(charts.reviewGrowth);
  charts.ratingTrend = arr(charts.ratingTrend);
  charts.sentiment = arr(charts.sentiment);
  charts.postActivity = arr(charts.postActivity);
  health.checks = arr(health.checks);

  const kpis = [
    { label: "Google Rating", value: cards.googleRating || "—", icon: Star, accent: "amber" },
    { label: "Total Reviews", value: cards.totalReviews, icon: MessageSquare, accent: "brand" },
    { label: "New This Month", value: cards.newReviewsThisMonth, icon: Activity, accent: "green" },
    { label: "Pending Replies", value: cards.pendingReplies, icon: MessageSquare, accent: "amber" },
    { label: "Negative Reviews", value: cards.negativeReviews, icon: AlertTriangle, accent: "red" },
    { label: "Scheduled Posts", value: cards.scheduledPosts, icon: CalendarClock, accent: "blue" },
    { label: "Published Posts", value: cards.publishedPosts, icon: FileText, accent: "green" },
    { label: "Review Requests", value: cards.reviewRequestsSent, icon: Send, accent: "purple" },
    { label: "Profile Health", value: `${cards.profileHealthScore}%`, icon: Activity, accent: "brand" },
    { label: "Days to Expiry", value: cards.daysToExpiry ?? "—", icon: CalendarClock, accent: "amber" },
  ];

  const quickActions = [
    { label: "Create Post", icon: FileText, to: "/app/posts" },
    { label: "AI Post", icon: Sparkles, to: "/app/ai-post" },
    { label: "Sync Reviews", icon: RefreshCw, to: "/app/reviews" },
    { label: "Generate QR", icon: QrCode, to: "/app/qr" },
    { label: "Review Request", icon: Send, to: "/app/review-requests" },
    { label: "Connect Google", icon: Link2, to: "/app/google" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`} subtitle="Here's your Google Business Profile performance at a glance" />

      <div className="flex flex-wrap gap-2">
        {quickActions.map((a) => (
          <Button key={a.label} variant="outline" onClick={() => navigate(a.to)} data-testid={`quick-${a.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-xl border-slate-200 hover:border-brand-400 hover:text-brand-700">
            <a.icon className="mr-1.5 h-4 w-4" /> {a.label}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {kpis.map((k) => <StatCard key={k.label} {...k} testId={`client-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`} />)}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <ChartCard title="Review Growth" subtitle="Reviews received per month">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={charts.reviewGrowth}>
              <defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4e67eb" stopOpacity={0.3} /><stop offset="95%" stopColor="#4e67eb" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip {...chartTooltipStyle()} />
              <Area type="monotone" dataKey="reviews" stroke="#4e67eb" strokeWidth={2.5} fill="url(#rg)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Rating Trend" subtitle="Average rating per month">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={charts.ratingTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip {...chartTooltipStyle()} />
              <Line type="monotone" dataKey="rating" stroke="#291d89" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Review Sentiment">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={charts.sentiment} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}>
                {arr(charts?.sentiment).map((_, i) => <Cell key={i} fill={SENTIMENT_COLORS[i]} />)}
              </Pie>
              <Tooltip {...chartTooltipStyle()} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Post Activity" subtitle="Published posts per month">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={charts.postActivity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip {...chartTooltipStyle()} />
              <Bar dataKey="posts" fill="#291d89" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title="Profile Health Score" subtitle={`${health.score}% complete — click any item to fix it`}>
        <div className="mb-4 h-3 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full gradient-brand" style={{ width: `${health.score}%` }} />
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {arr(health?.checks).map((c, i) => {
            const inner = (
              <span className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all ${c.ok ? "bg-green-50 text-green-700" : "bg-slate-50 text-slate-500 hover:bg-amber-50 hover:text-amber-700"}`}>
                <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ${c.ok ? "bg-green-500 text-white" : "bg-slate-300 text-white"}`}>{c.ok ? "✓" : "!"}</span>
                {c.key}
              </span>
            );
            return c.link && !c.ok ? (
              <Link key={i} to={c.link}>{inner}</Link>
            ) : (
              <div key={i}>{inner}</div>
            );
          })}
        </div>
      </ChartCard>
    </div>
  );
}
